/**
 * CRT power-on boot animation.
 *
 * Played once on first canvas paint. The phases approximate a 1984 CRT
 * coming alive: bright flash → scanline sweep as the cathode warms → a
 * "PRESENTS" credit → the marquee wordmark settles into place.
 *
 * Visual-only. No audio (audio bus lands under `epic:runtime`). No PRNG —
 * the boot is presentational, doesn't need to be replay-deterministic.
 *
 * Skippable: any keydown/pointerdown/touchstart after 500ms resolves the
 * promise early. Caller should treat resolve as "boot done, render idle."
 */

import { present } from './compositor.js';

const PHOSPHOR_YELLOW = '#ffea00';
const PHOSPHOR_CYAN = '#00d9ff';
const SCREEN_BG = '#0b0e1a';

/** Window during which input is ignored. Prevents accidental skip. */
export const SKIP_LOCKOUT_MS = 500;

/** Phase durations in ms. Sum should be ≤ 2500 per spec-shell.md. */
export const PHASE_DURATIONS = {
  flash: 50,
  scanlineSweep: 300,
  presentsFade: 1200,
  wordmark: 450,
} as const;

/** Total boot duration assuming no skip. */
export const TOTAL_BOOT_MS =
  PHASE_DURATIONS.flash +
  PHASE_DURATIONS.scanlineSweep +
  PHASE_DURATIONS.presentsFade +
  PHASE_DURATIONS.wordmark;

/**
 * Optional injection points for testing. Production callers should leave
 * these undefined — they default to wall-clock + rAF + window listeners.
 */
export interface BootDeps {
  now?: () => number;
  requestFrame?: (cb: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
  /** Add a global listener for skip detection. Returns a removal fn. */
  onUserInput?: (cb: () => void) => () => void;
}

/**
 * Run the boot animation on the supplied 2D context. Resolves when the
 * full sequence completes OR when the user skips after the lockout window.
 */
export function runBootSequence(
  ctx: CanvasRenderingContext2D,
  deps: BootDeps = {}
): Promise<{ skipped: boolean }> {
  const now = deps.now ?? (() => performance.now());
  const requestFrame = deps.requestFrame ?? requestAnimationFrame.bind(globalThis);
  const cancelFrame = deps.cancelFrame ?? cancelAnimationFrame.bind(globalThis);
  const onUserInput = deps.onUserInput ?? defaultOnUserInput;

  ctx.imageSmoothingEnabled = false;

  const startTime = now();
  let rafHandle: number | null = null;
  let removeInputListener: (() => void) | null = null;
  let skipRequested = false;

  return new Promise<{ skipped: boolean }>((resolve) => {
    const finish = (skipped: boolean): void => {
      if (rafHandle !== null) {
        cancelFrame(rafHandle);
        rafHandle = null;
      }
      if (removeInputListener) {
        removeInputListener();
        removeInputListener = null;
      }
      // Final paint: the wordmark, fully opaque, idle.
      paintWordmark(ctx, 1);
      present();
      resolve({ skipped });
    };

    removeInputListener = onUserInput(() => {
      const elapsed = now() - startTime;
      if (elapsed >= SKIP_LOCKOUT_MS) {
        skipRequested = true;
      }
    });

    const tick = (): void => {
      const elapsed = now() - startTime;

      if (skipRequested) {
        finish(true);
        return;
      }

      if (elapsed >= TOTAL_BOOT_MS) {
        finish(false);
        return;
      }

      paintFrame(ctx, elapsed);
      present();
      rafHandle = requestFrame(tick);
    };

    rafHandle = requestFrame(tick);
  });
}

/**
 * Pick the active phase and dispatch to its painter. Each painter is a
 * pure function of (ctx, phaseElapsed, phaseDuration) — no shared state
 * between phases beyond what we paint directly to the canvas.
 */
function paintFrame(ctx: CanvasRenderingContext2D, totalElapsed: number): void {
  let phaseStart = 0;

  // Phase 1: power-on flash
  if (totalElapsed < phaseStart + PHASE_DURATIONS.flash) {
    paintFlash(ctx, totalElapsed - phaseStart);
    return;
  }
  phaseStart += PHASE_DURATIONS.flash;

  // Phase 2: scanline sweep
  if (totalElapsed < phaseStart + PHASE_DURATIONS.scanlineSweep) {
    paintScanlineSweep(
      ctx,
      totalElapsed - phaseStart,
      PHASE_DURATIONS.scanlineSweep
    );
    return;
  }
  phaseStart += PHASE_DURATIONS.scanlineSweep;

  // Phase 3: "PRESENTS" fade
  if (totalElapsed < phaseStart + PHASE_DURATIONS.presentsFade) {
    paintPresents(
      ctx,
      totalElapsed - phaseStart,
      PHASE_DURATIONS.presentsFade
    );
    return;
  }
  phaseStart += PHASE_DURATIONS.presentsFade;

  // Phase 4: wordmark fade-up
  const wordmarkElapsed = totalElapsed - phaseStart;
  paintWordmarkFade(ctx, wordmarkElapsed, PHASE_DURATIONS.wordmark);
}

function paintFlash(ctx: CanvasRenderingContext2D, elapsed: number): void {
  // Quick decay from full white to black across the 50ms.
  const t = Math.min(1, elapsed / PHASE_DURATIONS.flash);
  const intensity = Math.floor(255 * (1 - t));
  ctx.fillStyle = `rgb(${intensity}, ${intensity}, ${intensity})`;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

function paintScanlineSweep(
  ctx: CanvasRenderingContext2D,
  elapsed: number,
  duration: number
): void {
  // Black background, single bright cyan line scanning down,
  // with a faded trail above (covers the area already swept).
  const t = Math.min(1, elapsed / duration);
  const sweepY = Math.floor(t * ctx.canvas.height);

  ctx.fillStyle = SCREEN_BG;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Faded trail: redraw the swept region with a cyan tint at low alpha.
  ctx.fillStyle = 'rgba(0, 217, 255, 0.06)';
  ctx.fillRect(0, 0, ctx.canvas.width, sweepY);

  // The leading scanline itself: 2px tall, full cyan.
  ctx.fillStyle = PHOSPHOR_CYAN;
  ctx.fillRect(0, sweepY, ctx.canvas.width, 2);
}

function paintPresents(
  ctx: CanvasRenderingContext2D,
  elapsed: number,
  duration: number
): void {
  // 200ms fade in, 800ms hold, 200ms fade out.
  const fadeIn = 200;
  const fadeOut = 200;
  const hold = duration - fadeIn - fadeOut;
  let alpha: number;
  if (elapsed < fadeIn) {
    alpha = elapsed / fadeIn;
  } else if (elapsed < fadeIn + hold) {
    alpha = 1;
  } else {
    alpha = Math.max(0, 1 - (elapsed - fadeIn - hold) / fadeOut);
  }

  ctx.fillStyle = SCREEN_BG;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  ctx.globalAlpha = alpha;

  ctx.fillStyle = PHOSPHOR_CYAN;
  ctx.font = '10px ui-monospace, monospace';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(
    'ARCADIA AMUSEMENTS INC.',
    ctx.canvas.width / 2,
    ctx.canvas.height / 2 - 10
  );
  ctx.fillText('PRESENTS', ctx.canvas.width / 2, ctx.canvas.height / 2 + 6);

  ctx.globalAlpha = 1;
}

function paintWordmarkFade(
  ctx: CanvasRenderingContext2D,
  elapsed: number,
  duration: number
): void {
  const alpha = Math.min(1, elapsed / duration);
  paintWordmark(ctx, alpha);
}

function paintWordmark(ctx: CanvasRenderingContext2D, alpha: number): void {
  ctx.fillStyle = SCREEN_BG;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  ctx.globalAlpha = alpha;
  ctx.fillStyle = PHOSPHOR_YELLOW;
  ctx.font = '24px ui-monospace, monospace';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(
    "ARCADIA '84",
    ctx.canvas.width / 2,
    ctx.canvas.height / 2
  );
  ctx.globalAlpha = 1;
}

/**
 * Default skip listener — keydown / pointerdown / touchstart on window.
 * Returns a cleanup function.
 */
function defaultOnUserInput(cb: () => void): () => void {
  const handler = (): void => cb();
  window.addEventListener('keydown', handler, { passive: true });
  window.addEventListener('pointerdown', handler, { passive: true });
  window.addEventListener('touchstart', handler, { passive: true });
  return () => {
    window.removeEventListener('keydown', handler);
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('touchstart', handler);
  };
}
